/* jscs:disable maximumLineLength */
import { getNativeAppDeviceInfo, isAndroidApp, isIosApp } from './device';
/* eslint-disable max-len */
import { formatInsurerType } from './insurance';
import { isEmptyObject } from './object';
import { MINIMUM_HIGHLY_RATED_FOR, CLOSE_SOON_TIME, OPEN_SOON_TIME } from '../../config';
import moment from 'moment-timezone';
import {
  filterSlotsByAvailability,
  getTodayAndTomorrowSlots,
  hasAppointmentSoonAfterRequestedAppointmentTime,
  hasSlotsAvailable,
} from '../../core/location/slots';
import {
  NEXT_CARE_GROUP_ID,
  NEXT_CARE_SOLV_TESTING_GROUP_ID,
  URGENT_CARE_FOR_KIDS_LOCATION_IDS,
  URGENT_CARE_FOR_KIDS_GROUP_ID,
  METRO_URGENT_CARE_GROUP_ID,
  CHILDRENS_URGENT_CARE_GROUP_ID,
  CHILDREN_URGENT_CARE_CHICAGO_LOCATION_IDS,
  UNIVERSITY_URGENT_CARE_LOCATION_ID,
  POP_CARE_PLUS_IMMEDIATE_CARE_GROUP_ID,
  PORTLAND_URGENT_CARE_GROUP_ID,
  SMART_CARE_SF,
  CARE_FIRST_LOCATION_IDS,
  BUMPS_N_BRUISES_LOCATION_ID,
  MOBIL_MED,
  MULTICARE_GROUP_ID,
  URGENT_CARE_FOR_KIDS_TELEMED,
  LIBERTY_URGENT_CARE_TELEMED,
  SMART_CARE_VIRTUAL_VISIT,
  RENOWN_URGENT_CARE_GROUP_ID,
  PREMIER_HEALTH_GROUP_ID,
  URGENT_CARE_FOR_KIDS_EL_PASO_SCHOOL_DISTRICT_TELEMED,
  SMART_CARE_SCHOOL_DISTRICT,
  PRO_HEALTH_GROUP_ID,
  URGENT_CARE_FOR_KIDS_PSJA_TELEMED,
  URGENT_CARE_FOR_KIDS_TRAINING_TELEMED,
  RELIANT_VIRTUAL_VISIT,
  URGENT_CARE_FOR_KIDS_AISD_TELEMED,
  SMART_CARE_TEST,
} from '../../config/locations';
import { parseSqlDateTime, dateFormat } from './date';
import {
  TIME_FORMAT,
  TIME_MIDNIGHT,
  CLOSED,
  ORDERED_WEEK_DAYS,
  TYPE_PPO,
  TYPE_SELF,
  PLATFORM_TYPE_TRADITIONAL,
  TYPE_TRICARE,
  MARKETPLACE_DFW,
} from '../../constants';
import {
  PEDIATRIC_URGENT_CARE,
  URGENT_CARE,
  RETAIL_CLINIC,
  PRIMARY_CARE,
  OCCUPATIONAL_MEDICINE,
  EMERGENCY_ROOM,
  SPECIALTY_URGENT_CARE_CATEGORIES,
  TELEHEALTH,
  EMERGENCY_ROOMS,
  HOSPITAL,
  HOSPITALS,
  FREESTANDING_ER,
  HOSPITAL_ER,
  INTERNAL_MEDICINE,
  PEDIATRICIANS,
  FAMILY_DOCTOR,
  HOSPITAL_OWNED_URGENT_CARE,
} from '../../constants/category';
import { isEmpty } from './empty';
import { isEmptyArray } from './array';
import { isEmptyString, queryStringFromObject, sentenceCase } from './string';
import { safeGet } from './object';
import { UCL_LOCATION_CATEGORY_TYPES } from '../../constants/category';
import { is24Hours } from './time';
import {
  DENTIST_VALUE,
  getProviderTypeName,
  URGENT_CARE_VALUE,
} from '../../components/Home/Tiles/ProviderTypes';

const SELF_PAY_ONLY = 'self_pay_only';

const isSolvPartner = location => location && location.isSolvPartner;
const isBookable = location => location && location.isBookable;
const isViewable = location => location && location.isViewable;

const isInMarketPlace = location => location && location.isInMarketplace;

const isTraditionalLocation = location =>
  location && location.platformType === PLATFORM_TYPE_TRADITIONAL;

const isFutureBookingLocation = location =>
  location &&
  location.id &&
  [
    'gZdDYA', // smart care paperless
    'g15dEg', // tlc
    'AG99Jg', // columbia clinic
    'pyokK0', // Family Urgent Care – Chicago
    '07wlj0', // Family Urgent Care – Schererville
    '0r17Jg', // smart care directory 1
    'A2GBwp', // smart care directory 2
    'Aa86l0', // smart care directory 3
  ].indexOf(location.id) > -1;

const attributesByType = (values, attributeType) => {
  const facilities = {};
  for (const key in values) {
    if (values.hasOwnProperty(key)) {
      if (
        values[key].attribute_type === attributeType &&
        values[key].attribute_value !== 'no' &&
        values[key].attribute_value !== 'false'
      ) {
        facilities[key] = values[key];
      }
    }
  }

  return facilities;
};

const getBrands = location => safeGet(location, {})('brands');

const hasBrands = location => !isEmpty(getBrands(location));

const isNextInLineFeatureEnabled = location => location.hasNextInLine;

const isSelfPayOnly = location =>
  safeGet(location, false)(`acceptedInsuranceTypes.${SELF_PAY_ONLY}`);

const validFacilities = values => attributesByType(values, 'service');

const getLocationServices = location =>
  [].concat.apply([], Object.values(safeGet(location, [])('services')));

const getUCLAcceptedInsurances = location => safeGet(location, [])('acceptedInsurances');

export const getTraditionalLocationAcceptedInsurances = location =>
  safeGet(location, {})('acceptedInsurers').map(insurer => insurer.display_name);

const getCollectedUrgentCareAcceptedInsuranceTypes = (location, opts = {}) => {
  const acceptedInsuranceTypes = [TYPE_SELF];

  if (!isSelfPayOnly(location)) {
    Object.entries({
      ...safeGet(location, {})('acceptedInsuranceTypes'),
    }).forEach(([key, value]) => {
      if (value) {
        acceptedInsuranceTypes.push(key === TYPE_TRICARE ? key.toUpperCase() : key);
      }
    });

    if (!acceptedInsuranceTypes.includes(TYPE_PPO)) {
      acceptedInsuranceTypes.push(TYPE_PPO);
    }
  }

  if (isEmptyArray(acceptedInsuranceTypes)) {
    return acceptedInsuranceTypes;
  }

  if (opts.formatInsurerType) {
    return acceptedInsuranceTypes.map(formatInsurerType);
  }

  return acceptedInsuranceTypes;
};

/*
  Accepted insurances come from different sources
  IF TRADITIONAL LOCATION (dentist, optometrist etc) : they should come from
    getTraditionalLocationAcceptedInsurances method which looks at the  acceptedInsurers fields in
    the response
  IF NOT TRADITIONAL LOCATION:
     IF NOT SOLV PARTNER: we should look at the data coming from UCL (acceptedInsurances in the
     response). if that's present use that, otherwise fall back to the data that might be collected
     manually by OPS [most likely will be empty]
     IF SOLV PARTNER: OPS usually collect this information
       during onboarding, so we should trust this data coming from
       getCollectedUrgentCareAcceptedInsuranceTypes
       which looks at the acceptedInsuranceTypes field from the response
 */
const getAcceptedInsurances = location => {
  const collectedAcceptedInsuranceTypes = getCollectedUrgentCareAcceptedInsuranceTypes(location, {
    formatInsurerType: true,
  });
  const uclAcceptedInsurances = getUCLAcceptedInsurances(location);

  if (isTraditionalLocation(location)) {
    return getTraditionalLocationAcceptedInsurances(location);
  }

  if (!isSolvPartner(location) && !isEmpty(uclAcceptedInsurances)) {
    return uclAcceptedInsurances;
  }

  return collectedAcceptedInsuranceTypes;
};

const hasKnownHours = hours => hours && ORDERED_WEEK_DAYS.some(day => !isEmptyArray(hours[day]));

const validPrices = values => attributesByType(values, 'price');

const locationStarScore = location => {
  let starRating = 0;
  if (isSolvPartner(location)) {
    starRating = location.ratingSolv;
  } else {
    if (isEmptyObject(location.scores) || isEmptyObject(location.scores.aggregated)) {
      starRating = 0;
    }

    for (const score of location.scores.aggregated) {
      if (score.topic === 'overall') {
        starRating = score.score;
        break;
      }
    }
  }

  return isEmptyString(starRating) ? null : Number.parseFloat(starRating, 10);
};

const getLocationRatingAndRatingCount = location => {
  let ratingCount = 0;
  let rating = 0;
  if (isSolvPartner(location)) {
    ratingCount = location.ratingSolvCount;
    rating = location.ratingSolv;
  } else {
    ratingCount = location.ratingNonSolvCount;
    rating = location.ratingNonSolv;
  }

  return { ratingCount, rating: parseFloat(rating) };
};

/**
 * Filter ratings down to those we want to display in the
 * "Highly Rated For" section of the CDP.
 * @param {Array} ratings
 * @return {Array}
 */
const validHighlyRatedFor = ratings => {
  if (!ratings || ratings.constructor !== Array) {
    return [];
  }

  const filtered = ratings.filter(
    rating =>
      rating.score >= MINIMUM_HIGHLY_RATED_FOR &&
      rating.topic !== 'other' &&
      rating.topic !== 'overall'
  );
  for (const rating of filtered) {
    rating.label = rating.topic.replace('_', ' ');
    rating.label = rating.label.charAt(0).toUpperCase() + rating.label.slice(1);
  }

  return filtered;
};

const formatDisplayAddress = location => {
  const address = !isEmptyString(location.displayAddress)
    ? location.displayAddress
    : location.address;

  return location.subpremise
    ? `${address} ${location.subpremise}, ${location.city} ${location.state}, ${location.zipCode}`
    : `${address}, ${location.city} ${location.state}, ${location.zipCode}`;
};

const formatDisplayShortAddress = location => {
  const address = !isEmptyString(location.displayAddress)
    ? location.displayAddress
    : location.address;
  return location.subpremise
    ? `${address}, ${location.subpremise}, ${location.city}`
    : `${address}, ${location.city}`;
};

const formatCityAndState = location => `${location.city}, ${location.state}`;

const getFullDisplayAddress = location => {
  const address = !isEmptyString(location.displayAddress)
    ? location.displayAddress
    : location.address;
  return location.subpremise ? `${address}, ${location.subpremise}` : address;
};

const encodeCityName = city => city.split(/\s+/g).join('-');

const decodeCityName = city => city.split('-').join(' ');

const getTodayHours = location => {
  const dt = moment.tz(location.timeZone);
  const dayOfWeek = dt.format('dddd');
  if (!isEmptyObject(location) && !isEmptyObject(location.hours)) {
    if (!isEmptyObject(location.hours[dayOfWeek]) && !isEmptyObject(location.hours[dayOfWeek][0])) {
      const open = location.hours[dayOfWeek][0].fromTime;
      const close = location.hours[dayOfWeek][0].toTime;

      if (!isEmptyObject(open) && !isEmptyObject(close)) {
        return { fromTime: open, toTime: close };
      }
    }
  }

  return {};
};

const getTomorrowHours = location => {
  const dt = moment.tz(location.timeZone).add(1, 'day');
  const dayOfWeek = dt.format('dddd');
  if (!isEmptyObject(location) && !isEmptyObject(location.hours)) {
    if (!isEmptyObject(location.hours[dayOfWeek]) && !isEmptyObject(location.hours[dayOfWeek][0])) {
      const open = location.hours[dayOfWeek][0].fromTime;
      const close = location.hours[dayOfWeek][0].toTime;

      if (!isEmptyObject(open) && !isEmptyObject(close)) {
        return { fromTime: open, toTime: close };
      }
    }
  }

  return {};
};

const getAnyDateHours = (location, date) => {
  if (isEmptyObject(location)) return {};

  const dayOfWeek = moment(date)
    .tz(location.timeZone)
    .format('dddd');

  const hours = date.isBefore(
    moment()
      .tz(location.timeZone)
      .add(7, 'days'),
    'day'
  )
    ? location.hours
    : location.hoursDefault;

  if (!isEmptyObject(hours)) {
    if (!isEmptyObject(hours[dayOfWeek]) && !isEmptyObject(hours[dayOfWeek][0])) {
      const open = hours[dayOfWeek][0].fromTime;
      const close = hours[dayOfWeek][0].toTime;
      if (!isEmptyObject(open) && !isEmptyObject(close)) {
        return { fromTime: open, toTime: close };
      }
    }
  }

  return {};
};

const dateHasOpenHours = (location, date) => {
  const dayHours = getAnyDateHours(location, date);
  return dayHours.hasOwnProperty('fromTime') && dayHours.hasOwnProperty('toTime');
};

const isAlwaysOpen = location => {
  const todayHours = getTodayHours(location);
  if (!todayHours.hasOwnProperty('fromTime') || !todayHours.hasOwnProperty('toTime')) {
    return false;
  }

  const openTime = todayHours.fromTime;
  const closeTime = todayHours.toTime;

  return is24Hours(openTime, closeTime);
};

/**
 * Return the next date that a given location is open
 * @param {Object} location
 * @param {moment} after date to search after
 * @returns {moment} moment date of next available date
 */
const getNextOpenDate = (location, after = moment().tz(location.timeZone)) => {
  const openDate = after.clone();
  for (let daysAhead = 0; daysAhead <= 7; ++daysAhead)
    if (dateHasOpenHours(location, openDate.add(daysAhead && 1, 'days'))) break;

  return openDate;
};

const hasOpenHoursToday = location => {
  const todayHours = getTodayHours(location);
  return todayHours.hasOwnProperty('fromTime') && todayHours.hasOwnProperty('toTime');
};

const hasOpenHoursTomorrow = location => {
  const tomorrowHours = getTomorrowHours(location);
  return tomorrowHours.hasOwnProperty('fromTime') && tomorrowHours.hasOwnProperty('toTime');
};

const isClosedTodayAndTomorrow = location =>
  !hasOpenHoursToday(location) && !hasOpenHoursTomorrow(location);

const hasAvailabilityTodayOrTomorrow = (location, newBooking) => {
  const todayAndTomorrowSlots = getTodayAndTomorrowSlots(newBooking, location);
  const todaySlots = filterSlotsByAvailability(todayAndTomorrowSlots.today);
  const tomorrowSlots = filterSlotsByAvailability(todayAndTomorrowSlots.tomorrow);
  return hasSlotsAvailable(todaySlots) || hasSlotsAvailable(tomorrowSlots);
};

const isOpenNow = location => {
  const todayHours = getTodayHours(location);
  if (!todayHours.hasOwnProperty('fromTime') || !todayHours.hasOwnProperty('toTime')) {
    return false;
  }

  const openTime = todayHours.fromTime;
  const closeTime = todayHours.toTime;

  const todayString = moment.tz(location.timeZone).format('YYYY-MM-DD');
  const openMoment = moment.tz(
    `${todayString} ${openTime}`,
    'YYYY-MM-DD HH:mm:ss',
    location.timeZone
  );

  let closeMoment = null;

  // make close time 11:59:59 if clinic closes at midnight so that isbetween can compare correctly
  if (closeTime === TIME_MIDNIGHT) {
    closeMoment = moment.tz(`${todayString} 23:59:59`, 'YYYY-MM-DD HH:mm:ss', location.timeZone);
  } else {
    closeMoment = moment.tz(
      `${todayString} ${closeTime}`,
      'YYYY-MM-DD HH:mm:ss',
      location.timeZone
    );
  }

  return moment.tz().isBetween(openMoment, closeMoment);
};

const reservationsDisabledNow = location => moment.tz().isBefore(location.disableReservationsUntil);

const isOpenSoon = location => {
  const tz = location.timeZone;
  const todayHours = getTodayHours(location);
  if (!todayHours.hasOwnProperty('fromTime')) {
    return false;
  }

  const openTime = moment.tz(todayHours.fromTime, 'HH:mm:ss', tz);

  return moment.tz(tz).isBetween(moment(openTime).subtract(OPEN_SOON_TIME, 'hours'), openTime);
};

const isClosingSoon = location => {
  const tz = location.timeZone;
  const todayHours = getTodayHours(location);
  if (!todayHours.hasOwnProperty('fromTime')) {
    return false;
  }

  let closeTime = null;
  if (todayHours.toTime === TIME_MIDNIGHT) {
    closeTime = moment.tz('23:59:59', 'HH:mm:ss', tz);
  } else {
    closeTime = moment.tz(todayHours.toTime, 'HH:mm:ss', tz);
  }

  return moment.tz(tz).isBetween(moment(closeTime).subtract(CLOSE_SOON_TIME, 'hours'), closeTime);
};

const isOutOfBusiness = location =>
  !isEmpty(location.status) ? location.status === CLOSED : false;

const validPriceList = values => {
  const prices = validPrices(values);
  const priceList = [];
  for (const attribute in prices) {
    if (prices.hasOwnProperty(attribute)) {
      if (!isEmptyObject(prices[attribute].attribute_value)) {
        priceList.push(parseInt(prices[attribute].attribute_value, 10));
      }
    }
  }

  return priceList;
};

const minPrice = values => Math.min(...validPriceList(values));

const maxPrice = values => Math.max(...validPriceList(values));

const haveDrivingDistance = (locationId, distanceMatrix) =>
  locationId &&
  distanceMatrix &&
  distanceMatrix.results &&
  distanceMatrix.results.hasOwnProperty(locationId);

const getDrivingDistance = (locationId, distanceMatrix) =>
  safeGet(distanceMatrix)(`results.${locationId}.distance.text`);

const isGoodEnoughMatch = (location, appointmentTime) =>
  hasAppointmentSoonAfterRequestedAppointmentTime(location, appointmentTime);

const formatLocationName = (location = {}, separator = ' - ') => {
  const displayNamePrimary = location.displayNamePrimary;
  if (location.displayNameSecondary) {
    return `${displayNamePrimary}${separator}${location.displayNameSecondary}`;
  }

  return displayNamePrimary;
};

const formatLocationSpecialties = location =>
  location && !isEmptyArray(location.specialties)
    ? location.specialties.reduce((a, c) => (a ? `${a}, ${c.display_name}` : c.display_name), '')
    : '';

const getLocationSpecialties = location => safeGet(location, [])('specialties');

const getLocationSpecialtyValue = location => {
  const specialties = getLocationSpecialties(location);
  if (!specialties || isEmptyArray(specialties)) return URGENT_CARE_VALUE;

  const specialtyValues = specialties.map(specialty => specialty.name);
  // Since a location might have multiple specialties, pick the first one.
  // If no specialty provided - assume Urgent Care
  return specialtyValues[0];
};

const getLocationSpecialtyName = location => {
  const value = getLocationSpecialtyValue(location);
  return getProviderTypeName(value);
};

const isDentalLocation = location => getLocationSpecialtyValue(location) === DENTIST_VALUE;

const formatLocationCategory = location => {
  let chosenCategory = null;

  if (isEmptyArray(location.category)) {
    return 'Urgent Care';
  }

  const locationCategories = location.category.map(category =>
    sentenceCase(category.replace(/_/g, ' '))
  );

  for (let i = 0; i <= UCL_LOCATION_CATEGORY_TYPES.length; i++) {
    if (locationCategories.includes(UCL_LOCATION_CATEGORY_TYPES[i])) {
      chosenCategory = UCL_LOCATION_CATEGORY_TYPES[i];
      break;
    }
  }

  if (
    locationCategories.includes(FREESTANDING_ER) ||
    locationCategories.includes(HOSPITAL) ||
    locationCategories.includes(HOSPITAL_ER) ||
    locationCategories.includes(HOSPITALS) ||
    locationCategories.includes(EMERGENCY_ROOMS)
  ) {
    return EMERGENCY_ROOM;
  }

  if (
    locationCategories.includes(INTERNAL_MEDICINE) ||
    locationCategories.includes(FAMILY_DOCTOR) ||
    locationCategories.includes(PEDIATRICIANS)
  ) {
    return PRIMARY_CARE;
  }

  if (locationCategories.includes(HOSPITAL_OWNED_URGENT_CARE)) {
    return URGENT_CARE;
  }

  return chosenCategory || locationCategories[0];
};

const formatLocationNameFromBooking = (booking, separator = '-') => {
  const displayNamePrimary = booking.location_display_name_primary;
  if (booking.location_display_name_secondary) {
    return `${displayNamePrimary} ${separator} ${booking.location_display_name_secondary}`;
  }

  return displayNamePrimary;
};

const formatLocationNameFromInvoice = invoice => {
  if (isEmptyObject(invoice)) {
    return '';
  }

  const displayNamePrimary = invoice.display_name_primary;

  if (invoice.display_name_secondary) {
    return `${displayNamePrimary} - ${invoice.display_name_secondary}`;
  }

  return displayNamePrimary;
};

const isUrgentCareForKids = locationId => URGENT_CARE_FOR_KIDS_LOCATION_IDS.includes(locationId);

const isUrgentCareForKidsTelemed = location => location.id === URGENT_CARE_FOR_KIDS_TELEMED;

const isCareFirstLocation = locationId => CARE_FIRST_LOCATION_IDS.includes(locationId);

const isChildrenUrgentCareChicago = locationId =>
  CHILDREN_URGENT_CARE_CHICAGO_LOCATION_IDS.includes(locationId);

const isUniversityUrgentCare = locationId => locationId === UNIVERSITY_URGENT_CARE_LOCATION_ID;

const isSmartCareSF = locationId => SMART_CARE_SF === locationId;

const isSmartCareTest = location => location.id === SMART_CARE_TEST;

const getOpenHoursString = ({ location, date }) => {
  const appointmentIsoDate = dateFormat(date, 'isoDate');
  const dayName = dateFormat(date, 'dddd');
  const hours = location.hours[dayName];

  const formattedHours = hours.map(interval => {
    const startTime = parseSqlDateTime(`${appointmentIsoDate} ${interval.fromTime}`);
    const endTime = parseSqlDateTime(`${appointmentIsoDate} ${interval.toTime}`);
    return `${dateFormat(startTime, TIME_FORMAT)} to ${dateFormat(endTime, TIME_FORMAT)}`;
  });

  return formattedHours.join(', ');
};

const hasHours = location => {
  if (isEmptyObject(location.hours)) {
    return false;
  }

  const hours = location.hours;
  for (const day in hours) {
    if (hours.hasOwnProperty(day)) {
      if (!isEmptyArray(hours[day])) {
        return true;
      }
    }
  }

  return false;
};

const isInGroup = (location, groupId) =>
  !isEmptyArray(location.groups) && location.groups.some(group => group.group_id === groupId);

const isConfiguredForExternalTracking = location => !isEmptyObject(location.trackingProperties);

const isPartOfNextCareGroup = (location, includeTest) => {
  if (!includeTest) {
    return isInGroup(location, NEXT_CARE_GROUP_ID);
  }

  return (
    isInGroup(location, NEXT_CARE_GROUP_ID) || isInGroup(location, NEXT_CARE_SOLV_TESTING_GROUP_ID)
  );
};

const isPartOfUrgentCareForKidsGroup = location =>
  isInGroup(location, URGENT_CARE_FOR_KIDS_GROUP_ID);

const isPediatricPatientsAccepted = location => location.isPediatricPatientsAccepted;

const isAdultPatientsAccepted = location => location.isAdultPatientsAccepted;

const isPartOfMetroUrgentCareGroup = location => isInGroup(location, METRO_URGENT_CARE_GROUP_ID);

const isPartOfChildrensUrgentCareGroup = location =>
  isInGroup(location, CHILDRENS_URGENT_CARE_GROUP_ID);

const isPartOfPremierHealthGroup = location => isInGroup(location, PREMIER_HEALTH_GROUP_ID);

const isPartOfPopCarePlusImmediateCareGroup = location =>
  isInGroup(location, POP_CARE_PLUS_IMMEDIATE_CARE_GROUP_ID);

const isPartOfPortlandUrgentCareGroup = location =>
  isInGroup(location, PORTLAND_URGENT_CARE_GROUP_ID);

const isPartOfMulticareGroup = location => isInGroup(location, MULTICARE_GROUP_ID);

const isPartOfRenownGroup = location => isInGroup(location, RENOWN_URGENT_CARE_GROUP_ID);

const isTelemedLocation = location => location.isTelemed;

const isTokboxTelemedLocation = location => !isEmptyObject(location) && location.isTokboxEnabled;

const shouldShowVisitTimeDisclaimer = location =>
  !isTelemedLocation(location) &&
  (isPartOfNextCareGroup(location) ||
    isPartOfUrgentCareForKidsGroup(location) ||
    isPartOfChildrensUrgentCareGroup(location));

const isBumpsNBruisesLocation = location => location.id === BUMPS_N_BRUISES_LOCATION_ID;

const shouldRequireEmailInBookingWidget = location => isPartOfNextCareGroup(location);

const isUberEnabled = location => location.isUberEnabled;

const isPaperworkEnabled = location => location.isPaperworkEnabled;

const isBus = location => location.id === MOBIL_MED;

const isUrgentCare = location => formatLocationCategory(location) === URGENT_CARE;
const isPediatricUrgentCare = location =>
  location.isPediatricsOnly || formatLocationCategory(location) === PEDIATRIC_URGENT_CARE;
const isRetailClinic = location => formatLocationCategory(location) === RETAIL_CLINIC;
const isPrimaryCare = location => formatLocationCategory(location) === PRIMARY_CARE;
const isOccupationalHealth = location => formatLocationCategory(location) === OCCUPATIONAL_MEDICINE;
const isEmergencyRoom = location => formatLocationCategory(location) === EMERGENCY_ROOM;
const isSpecialtyUrgentCare = location =>
  SPECIALTY_URGENT_CARE_CATEGORIES.includes(formatLocationCategory(location));
const isTelehealth = location => formatLocationCategory(location) === TELEHEALTH;

const getLocationAttribute = (location, attribute) => {
  if (!location || isEmpty(location.attributes) || isEmpty(location.attributes[attribute])) {
    return null;
  }

  const value = location.attributes[attribute].attributeValue;

  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  }

  return value;
};

/**
 * Returns the service price given the requested service name if found.
 * @param {Array} servicePrices
 * @param {String} serviceName
 * @returns {number}
 */
const getCashPrice = (servicePrices, serviceName) => {
  const requestedService = (servicePrices || []).filter(
    service => service.service_name === serviceName
  )[0];

  return requestedService && requestedService.price;
};

const isV1Description = location => location.descriptionVariationTest === 'v1';
const isV2Description = location => location.descriptionVariationTest === 'v2';
const isV3Description = location => location.descriptionVariationTest === 'v3';

const isUrgentCareV1 = location => isUrgentCare(location) && isV1Description(location);
const isUrgentCareV2 = location => isUrgentCare(location) && isV2Description(location);
const isUrgentCareV3 = location => isUrgentCare(location) && isV3Description(location);

const getGoogleDirectionsUrl = location => {
  let protocol = 'https';
  if (isIosApp()) {
    protocol = 'maps'; // maps:// or geo:// vs https:// is so that it can link to a native app
  } else if (isAndroidApp()) {
    protocol = 'geo';
  }

  const baseUrl = `${protocol}://maps.google.com/`;
  const address = `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`;
  const params = {
    f: 'd', // set layout format to directions
    ll: `${location.latLong.latitude},${location.latLong.longitude}`,
    view: 'map',
    q: location.displayNamePrimary,
    daddr: address,
    address,
  };

  return `${baseUrl}?${queryStringFromObject(params)}`;
};

/**
 * Return the locations latitude and longitude
 * @param location
 * @returns {{latitude: {Number}, longitude: {Number}}}
 */
const getLatitudeLongitude = location => {
  const { latitude, longitude } = (location && location.latLong) || {};
  const lat = parseFloat(latitude, 10);
  const lng = parseFloat(longitude, 10);

  if (isNaN(lat) || isNaN(lng)) {
    return null;
  }

  return { latitude: lat, longitude: lng };
};

const getLatLongStr = location => {
  const latLong = getLatitudeLongitude(location);

  return `${latLong.latitude},${latLong.longitude}`;
};

const getGroupId = location => (isEmptyArray(location.groups) ? null : location.groups[0].group_id);

const shouldShowBirthSexInput = location =>
  isPartOfUrgentCareForKidsGroup(location) || isPartOfPremierHealthGroup(location);

const getLocationBasePrice = location => {
  if (!location.servicePrices) {
    return null;
  }

  let basePrice = null;

  location.servicePrices.forEach(service => {
    if (service.service_name === 'base_price') {
      basePrice = service.price;
    }
  });

  return basePrice;
};

const isUrgentCareForKidsTelemedInSchoolsLocation = location =>
  [
    URGENT_CARE_FOR_KIDS_AISD_TELEMED,
    URGENT_CARE_FOR_KIDS_EL_PASO_SCHOOL_DISTRICT_TELEMED,
    URGENT_CARE_FOR_KIDS_PSJA_TELEMED,
    URGENT_CARE_FOR_KIDS_TRAINING_TELEMED,
  ].includes(location.id);

const isSmartCareSchoolDistrict = location => location.id === SMART_CARE_SCHOOL_DISTRICT;

const shouldRenderDirectTelemedFlow = location =>
  isUrgentCareForKidsTelemedInSchoolsLocation(location) || isSmartCareSchoolDistrict(location);

const isInDfwMarket = location => location.market === MARKETPLACE_DFW;

const isLocationConsentFlowEnabled = location => location.isConsentEnabled;

const getLocationGroupId = location =>
  location && location.groups && location.groups[0] && location.groups[0].group_id;

const getDisplayNamePrimaryWithSpecialProHealthRule = location =>
  isInGroup(location, PRO_HEALTH_GROUP_ID) ? 'ProHEALTH Urgent Care' : location.displayNamePrimary;

const isReliantVirtualVisit = location => location.id === RELIANT_VIRTUAL_VISIT;

export {
  decodeCityName,
  encodeCityName,
  getGoogleDirectionsUrl,
  formatDisplayAddress,
  formatCityAndState,
  formatLocationCategory,
  formatLocationName,
  formatLocationNameFromBooking,
  formatLocationNameFromInvoice,
  formatDisplayShortAddress,
  getDisplayNamePrimaryWithSpecialProHealthRule,
  getGroupId,
  getLocationAttribute,
  getLocationSpecialtyValue,
  getLocationSpecialtyName,
  getTodayHours,
  getTomorrowHours,
  getLocationServices,
  hasHours,
  hasOpenHoursToday,
  hasOpenHoursTomorrow,
  getLocationRatingAndRatingCount,
  haveDrivingDistance,
  isUrgentCare,
  isUrgentCareForKidsTelemedInSchoolsLocation,
  isBumpsNBruisesLocation,
  isBookable,
  isClosedTodayAndTomorrow,
  isClosingSoon,
  isCareFirstLocation,
  isConfiguredForExternalTracking,
  isPediatricUrgentCare,
  isSmartCareSF,
  isSmartCareTest,
  isSolvPartner,
  isGoodEnoughMatch,
  isInGroup,
  isInDfwMarket,
  isInMarketPlace,
  isAlwaysOpen,
  isFutureBookingLocation,
  isOpenNow,
  isOpenSoon,
  isOutOfBusiness,
  isSelfPayOnly,
  isTelemedLocation,
  isNextInLineFeatureEnabled,
  isOccupationalHealth,
  isPartOfNextCareGroup,
  isPartOfMetroUrgentCareGroup,
  isRetailClinic,
  isUrgentCareForKids,
  isUrgentCareForKidsTelemed,
  isUrgentCareV1,
  isUrgentCareV2,
  isUrgentCareV3,
  isAdultPatientsAccepted,
  isPartOfUrgentCareForKidsGroup,
  isPartOfChildrensUrgentCareGroup,
  isPartOfPopCarePlusImmediateCareGroup,
  isPartOfPortlandUrgentCareGroup,
  isPartOfPremierHealthGroup,
  isPartOfMulticareGroup,
  isUniversityUrgentCare,
  isTelehealth,
  isChildrenUrgentCareChicago,
  isUberEnabled,
  isPaperworkEnabled,
  isPediatricPatientsAccepted,
  locationStarScore,
  maxPrice,
  minPrice,
  getBrands,
  getOpenHoursString,
  reservationsDisabledNow,
  hasBrands,
  hasKnownHours,
  shouldRequireEmailInBookingWidget,
  shouldShowVisitTimeDisclaimer,
  validFacilities,
  validHighlyRatedFor,
  validPriceList,
  validPrices,
  isTokboxTelemedLocation,
  isBus,
  isPrimaryCare,
  isEmergencyRoom,
  isDentalLocation,
  isSpecialtyUrgentCare,
  isViewable,
  getFullDisplayAddress,
  getAnyDateHours,
  dateHasOpenHours,
  getUCLAcceptedInsurances,
  isPartOfRenownGroup,
  getNextOpenDate,
  getCollectedUrgentCareAcceptedInsuranceTypes,
  getAcceptedInsurances,
  shouldShowBirthSexInput,
  getLocationBasePrice,
  getLocationSpecialties,
  getDrivingDistance,
  hasAvailabilityTodayOrTomorrow,
  getCashPrice,
  getLatitudeLongitude,
  getLatLongStr,
  isTraditionalLocation,
  formatLocationSpecialties,
  shouldRenderDirectTelemedFlow,
  isLocationConsentFlowEnabled,
  getLocationGroupId,
  isReliantVirtualVisit,
};
